import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { DateTimeWithoutYear, InvoiceStatuses } from "utilities/dist/invoice/constants";
import { isCompanyFeatureActive } from "utilities/dist/company/helpers";
import { CompanyFeatures } from "utilities/dist/company/constants";

import InvoiceComponentStatus from "../components/status";
import { cloneInvoice } from "../builder/actions";
import {
    CustomerInvoiceNumberingFeature,
    EmailFeatureDetails,
    isFeatureActive,
} from "../../company/feature/helpers";
import { generatePdf } from "../helpers";
import InvoiceEmailModal from "../builder/invoice-email.component";
import { buildInvoiceNumberWithBeer } from "utilities/dist/invoice/helpers";

export const InvoiceLinkRenderer = ({ value, data }) => {
    const isCustomerInvoiceNumberingActive = useSelector(({ auth }) =>
        isCompanyFeatureActive(auth.user.company, CompanyFeatures.CUSTOMER_INVOICE_NUMBERING)
    );

    if (!value) {
        return null;
    }

    const customerId = data?.customer_id || data?.invoice?.customer_id;

    const invoiceNumber =
        isCustomerInvoiceNumberingActive && customerId ? `${customerId}-${value}` : value;
    // grid values are parsed through valueGetter which adds B if inv is for beer so we replace that from link
    return <Link to={`/invoice/${invoiceNumber}`}>#{invoiceNumber}</Link>;
};

export const InvoiceStatusRenderer = ({ value }) => {
    return (
        <span className="tm-10 dib">
            <InvoiceComponentStatus size="mini" noIcon={true} status={value} />
        </span>
    );
};
export const InvoiceHistoryRenderer = ({ data, node }) => {
    if (node.rowPinned) {
        return null;
    }

    const statusPattern = /(\b[a-z](?!\s))/g;
    const invoiceStatus = data?.status?.replace(/_|payment/gi, " ");
    return (
        <>
            {data?.orders?.length ? (
                <Popup
                    trigger={<Icon name="cart" color="green" />}
                    content={`${format(
                        new Date(data.orders[0].created_at),
                        DateTimeWithoutYear
                    )} Order #${data.orders[0].order_number} placed`}
                    basic
                />
            ) : (
                ""
            )}{" "}
            {data?.statusInfo?.length > 0 ? (
                <Popup
                    trigger={<Icon name="newspaper" color="green" />}
                    content={
                        <>
                            <p>
                                {format(new Date(data.created_at), DateTimeWithoutYear)} - Invoice
                                created
                            </p>
                            <div>
                                {data.statusInfo.map(i => (
                                    <p key={i.id}>
                                        {format(new Date(i?.created_at), DateTimeWithoutYear)} -
                                        Status changed to{" "}
                                        <b>
                                            {i.new_status
                                                .replace(statusPattern, x => x.toUpperCase())
                                                .replace(/_|payment/gi, " ")}
                                        </b>{" "}
                                    </p>
                                ))}
                            </div>
                        </>
                    }
                    basic
                />
            ) : (
                <>
                    <Popup
                        trigger={<Icon name="newspaper" color="green" />}
                        content={
                            <>
                                {data?.created_at !== data?.updated_at ? (
                                    <>
                                        <p>
                                            {data?.created_at
                                                ? format(
                                                      new Date(data?.created_at),
                                                      DateTimeWithoutYear
                                                  )
                                                : ""}{" "}
                                            - Invoice created
                                        </p>
                                        <p>
                                            {data?.updated_at
                                                ? format(
                                                      new Date(data?.updated_at),
                                                      DateTimeWithoutYear
                                                  )
                                                : ""}{" "}
                                            - status changed to{" "}
                                            <b>
                                                {invoiceStatus.replace(statusPattern, x =>
                                                    x.toUpperCase()
                                                )}
                                            </b>
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p>
                                            {data?.created_at
                                                ? format(
                                                      new Date(data?.created_at),
                                                      DateTimeWithoutYear
                                                  )
                                                : " "}{" "}
                                            - Invoice created
                                        </p>
                                    </>
                                )}
                            </>
                        }
                        basic
                    />
                </>
            )}{" "}
            {data?.payments?.length ? (
                <Popup
                    trigger={<Icon name="payment" color="green" />}
                    content={`Payment has received at ${format(
                        new Date(data.payments[0].received_at),
                        DateTimeWithoutYear
                    )}`}
                    basic
                />
            ) : (
                <Popup
                    trigger={<Icon name="payment" color="grey" />}
                    content="No payments logged"
                    basic
                />
            )}
        </>
    );
};

export const InvoiceActionsRenderer = ({ data, node }) => {
    const dispatch = useDispatch();
    const handleClone = () => dispatch(cloneInvoice(data));
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);

    const { company } = useSelector(({ auth }) => ({
        company: auth.user.company,
    }));
    const hasEmailFeature = isFeatureActive(company, EmailFeatureDetails);
    const isCustomerInvoiceNumberingActive = isFeatureActive(
        company,
        CustomerInvoiceNumberingFeature
    );
    const canEdit = [InvoiceStatuses.DRAFT, InvoiceStatuses.READY_FOR_PICK].includes(data?.status);

    if (node.rowPinned) {
        return null;
    }

    if (!data) {
        return null;
    }

    return (
        <>
            <Link
                to="/invoice"
                title="Download Pdf"
                onClick={async e => {
                    e.preventDefault();
                    if (generatingPdf) return;
                    await generatePdf(
                        buildInvoiceNumberWithBeer(data, isCustomerInvoiceNumberingActive),
                        setGeneratingPdf
                    );
                }}
            >
                <Icon disabled={generatingPdf} name={generatingPdf ? "spinner" : "file"} />
            </Link>
            {canEdit ? (
                <Link
                    title="Edit Invoice"
                    to={`/invoice/${buildInvoiceNumberWithBeer(
                        data,
                        isCustomerInvoiceNumberingActive
                    )}/edit`}
                >
                    <Icon name="pencil" />
                </Link>
            ) : (
                <Popup
                    content="Only draft invoices can be edited"
                    trigger={<Icon name="pencil" disabled style={{ opacity: 0.5 }} />}
                />
            )}
            <Link title="Clone Invoice" onClick={handleClone} to={`/invoice/create`}>
                <Icon name="copy" />
            </Link>
            {hasEmailFeature && (
                <>
                    <Link
                        title="Email"
                        onClick={e => {
                            e.preventDefault();
                            setEmailModalOpen(true);
                        }}
                        to="/invoice"
                    >
                        <Icon name="envelope" />
                    </Link>
                    <InvoiceEmailModal
                        invoice={data}
                        open={emailModalOpen}
                        setOpen={setEmailModalOpen}
                    />
                </>
            )}
        </>
    );
};

export const InvoiceCustomerRenderer = ({ data }) => {
    if (!data || !data.customer) return null;
    return (
        <>
            {data.customer.name}
            <br />
            {data.customer.address}
        </>
    );
};

export const InvoiceEmptyRenderer = () => null;
